import { formatVolume, formatWeight } from "./units.js";
import { formatKcal } from "./sessionSummaryMath.js";
import type { ShareCardEnergy, ShareCardLabels } from "./shareCard.js";

// The share card's private formatters, split out of shareCard.ts
// (SP-SHARECARD, which was 456 lines). All five were module-private and
// nothing outside shareCard.ts ever called them, so no external contract
// moves with them: this module is NOT added to the package barrel, and the
// package's public surface is byte-for-byte what it was.
//
// Three of the five gained an `export` keyword — the only edit to the moved
// code — because shareCard.ts still calls them. formatTut and
// formatImpulseCompact stay private here: their only callers are their two
// siblings below, so the split actually narrowed their visibility rather
// than widening it.
//
// The import of ShareCardEnergy/ShareCardLabels from shareCard.ts is
// deliberately `import type`. shareCard.ts imports these functions as
// VALUES, so a value-level back-import would be a real cycle; a type-only
// one is erased at compile time, leaving the emitted module graph
// one-directional (shareCard -> shareCardFormat) with no load-order
// hazard. The types stay in shareCard.ts because they are its public
// vocabulary — callers build the labels bundle and pass it in.

// Format minutes → "Xh Ym" with no decimals.
export const formatDuration = (
  minutes: number | null,
  labels: ShareCardLabels,
): string => {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) return "";
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} ${labels.durationMinute}`;
  return `${h}${labels.durationHour} ${m}${labels.durationMinuteShort}`;
};

// Time under tension seconds → "m:ss" (or "h:mm:ss" past an hour).
const formatTut = (sec: number): string => {
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

// Impulse (kg·s) → compact "2.5k" / "850".
const formatImpulseCompact = (kgS: number): string =>
  kgS >= 1000 ? `${(kgS / 1000).toFixed(1)}k` : `${Math.round(kgS)}`;

// Compact a footer "12 sets · 84 min · 18.9k kg · 4:30 TUT · 2.5k kg·s ·
// 225 kg/min" string. Each segment is added only when it carries
// information; empty segments dropped. TUT + impulse appear only when the
// session has timed/isometric work (the isometric load model, 2026-06-09)
// so a pure-weights session footer is unchanged.
export const formatFooter = (
  durationMinutes: number | null,
  totalVolumeKg: number,
  densityKgPerMin: number | null,
  totalSets: number,
  totalTutSec: number,
  totalImpulseKgS: number,
  unit: "kg" | "lb",
  locale: string,
  labels: ShareCardLabels,
): string => {
  const segments: string[] = [];
  if (totalSets > 0) {
    segments.push(`${totalSets} ${labels.footerSets}`);
  }
  const dur = formatDuration(durationMinutes, labels);
  if (dur) segments.push(dur);
  if (totalVolumeKg > 0) {
    segments.push(formatVolume(totalVolumeKg, unit, locale));
  }
  if (totalTutSec > 0) {
    segments.push(`${formatTut(totalTutSec)} ${labels.tutLabel}`);
  }
  if (totalImpulseKgS > 0) {
    segments.push(`${formatImpulseCompact(totalImpulseKgS)} ${labels.impulseUnit}`);
  }
  if (densityKgPerMin !== null && densityKgPerMin > 0) {
    segments.push(
      `${formatWeight(densityKgPerMin, unit, locale)}${labels.footerDensitySuffix}`,
    );
  }
  return segments.join("  ·  ");
};

// Structured footer STAT-STRIP (HC-8 B3). The same stats as the dense
// `formatFooter` line minus the sets count, as labelled value-over-label cells,
// with Calories + Effort appended from the resolved energy. Each cell is pushed
// only when it carries information → a plain strength session yields exactly
// Volume/Duration/Density/Calories/Effort; a timed/isometric one also gets
// TUT/Impulse (no regression vs the dense line).
export const buildFooterStats = (
  durationMinutes: number | null,
  totalVolumeKg: number,
  densityKgPerMin: number | null,
  totalTutSec: number,
  totalImpulseKgS: number,
  energy: ShareCardEnergy | undefined,
  unit: "kg" | "lb",
  locale: string,
  labels: ShareCardLabels,
): Array<{ label: string; value: string }> => {
  const cells: Array<{ label: string; value: string }> = [];
  if (totalVolumeKg > 0) {
    cells.push({
      label: labels.volumeLabel,
      value: formatVolume(totalVolumeKg, unit, locale),
    });
  }
  const dur = formatDuration(durationMinutes, labels);
  if (dur) cells.push({ label: labels.durationLabel, value: dur });
  if (densityKgPerMin !== null && densityKgPerMin > 0) {
    cells.push({
      label: labels.densityLabel,
      value: `${formatWeight(densityKgPerMin, unit, locale)}${labels.footerDensitySuffix}`,
    });
  }
  if (totalTutSec > 0) {
    cells.push({ label: labels.tutLabel, value: formatTut(totalTutSec) });
  }
  if (totalImpulseKgS > 0) {
    cells.push({
      label: labels.impulseLabel,
      value: `${formatImpulseCompact(totalImpulseKgS)} ${labels.impulseUnit}`,
    });
  }
  const kcal = energy?.kcal;
  if (kcal !== undefined && kcal !== null && Number.isFinite(kcal)) {
    cells.push({ label: labels.caloriesLabel, value: formatKcal(kcal) });
  }
  if (energy?.effortText) {
    cells.push({ label: labels.effortLabel, value: energy.effortText });
  }
  return cells;
};
